import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { Dialog, type DialogProps } from '../../features/dialog/dialog.tsx';
import { cn } from '../../lib/utils.ts';
import { Button } from './button.tsx';
import { inputVariants } from './input.tsx';

type CommandDialogContextValue = {
    onClose?: () => void;
};

const CommandDialogContext = React.createContext<CommandDialogContextValue>({});

const Command = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
    <CommandPrimitive
        ref={ref}
        className={cn(
            'flex gap-3',
            'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
            '**:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:font-medium',
            '[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0',
            '**:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-2 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5',
            className,
        )}
        {...props}
    />
));
Command.displayName = CommandPrimitive.displayName;

type CommandDialogProps = {
    shouldFilter?: boolean;
    value?: string;
    filter?: (value: string, search: string, keywords?: string[]) => number;
} & DialogProps;

const CommandDialog = ({
    children,
    shouldFilter = true,
    value,
    filter,
    onClose,
    onOpenChange,
    ...props
}: CommandDialogProps) => {
    const handleClose = React.useCallback(() => {
        onClose?.();
        onOpenChange?.(false);
    }, [onClose, onOpenChange]);

    // Workaround for HeadlessUI disabling autofocus on touch devices
    // See: https://github.com/tailwindlabs/headlessui/issues/3713
    const handleFocus = React.useCallback(() => {
        const input = document.querySelector('[cmdk-input]') as HTMLInputElement | null;
        input?.focus();
    }, []);

    return (
        <Dialog {...props} onClose={onClose} onOpenChange={onOpenChange} onFocus={handleFocus}>
            <CommandDialogContext.Provider value={{ onClose: handleClose }}>
                <Command value={value} filter={filter} shouldFilter={shouldFilter}>
                    {children}
                </Command>
            </CommandDialogContext.Provider>
        </Dialog>
    );
};

const CommandInput = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Input>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
        showCancelButton?: boolean;
        cancelButtonLabel?: string;
    }
>(
    (
        {
            className,
            showCancelButton = true,
            cancelButtonLabel = 'Abbrechen',
            value,
            onValueChange,
            ...props
        },
        ref,
    ) => {
        const { onClose } = React.useContext(CommandDialogContext);
        const showClear = !!value && value.length > 0;

        const handleClear = () => {
            onValueChange?.('');
        };

        return (
            <div className={cn('flex items-center gap-2 border-b border-neutral-200 px-4 py-3', className)}>
                <div className="relative flex flex-1 items-center">
                    <SearchIcon className="absolute left-2 h-4 w-4 shrink-0 text-neutral-400" />
                    <CommandPrimitive.Input
                        ref={ref}
                        value={value}
                        onValueChange={onValueChange}
                        className={cn(
                            inputVariants({ hasLeftIcon: true, hasRightIcon: showClear }),
                            'outline-none',
                        )}
                        autoFocus
                        {...props}
                    />
                    {showClear && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute right-2 flex items-center"
                        >
                            <XIcon className="h-4 w-4 text-neutral-400" />
                        </button>
                    )}
                </div>
                {showCancelButton && onClose && (
                    <Button variant="link" onClick={onClose}>
                        {cancelButtonLabel}
                    </Button>
                )}
            </div>
        );
    },
);

CommandInput.displayName = 'CommandInput';

const CommandList = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.List
        ref={ref}
        className={cn(
            'flex max-h-[70vh] min-h-8 flex-1',
            'overflow-x-hidden overflow-y-auto',
            'sm:max-h-72 **:[[cmdk-list-sizer]]:w-full',
            className,
        )}
        {...props}
    />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Empty>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <CommandPrimitive.Empty ref={ref} className="px-4 text-center text-base" {...props} />);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Group>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.Group
        ref={ref}
        className={cn(
            'w-full text-foreground px-4 pb-3 overflow-hidden',
            '**:[[cmdk-group-heading]]:text-muted-foreground',
            '**:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-sm',
            '**:[[cmdk-group-heading]]:font-medium',
            '**:[[cmdk-group-items]]:flex **:[[cmdk-group-items]]:flex-col **:[[cmdk-group-items]]:gap-1',
            className,
        )}
        {...props}
    />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.Separator ref={ref} className={cn('bg-border -mx-1 h-px', className)} {...props} />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.Item
        ref={ref}
        className={cn(
            'hover:bg-accent aria-selected:bg-accent aria-selected:text-accent-foreground relative flex cursor-default',
            'select-none items-center rounded-lg px-2 text-sm',
            'outline-hidden data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-50',
            className,
        )}
        {...props}
    />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    return (
        <span className={cn('text-muted-foreground ml-auto text-sm tracking-widest', className)} {...props} />
    );
};
CommandShortcut.displayName = 'CommandShortcut';

const CommandLoading = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Loading>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
    // @ts-ignorets-ignore
>((props, ref) => <CommandPrimitive.Loading ref={ref} className="px-4 text-center text-base" {...props} />);
CommandLoading.displayName = CommandPrimitive.Loading.displayName;

export {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandLoading,
    CommandSeparator,
    CommandShortcut,
};
